import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import Logo from '@/components/Logo';
import { useToast } from '@/hooks/use-toast';
import { saveSurveyResponse } from '@/services/surveyService';
import { calculateScoreBreakdown } from '@/utils/scoreCalculator';
import { ScoreBreakdown } from '@/types/survey';
import { 
  Download, 
  ArrowLeft, 
  PieChart, 
  FileText, 
  Info, 
  ChevronRight,
  Activity,
  BarChart2,
  LayoutDashboard,
  AlertCircle,
  HelpCircle,
  ChevronDown,
  ShieldAlert,
  Trophy,
  Award,
  Star
} from 'lucide-react';
import { generatePdf } from '@/utils/pdfGenerator';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useAuth } from '@/contexts/AuthContext';
import ResultsChart from '@/components/ResultsChart';
import ProfessionalDashboard from '@/components/dashboards/ProfessionalDashboard';
import AIInsights from '@/components/AIInsights';
import { questions } from '@/data/questions';
import MethodologySection from '@/components/MethodologySection';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Alert, AlertDescription } from '@/components/ui/alert';
import ScoreCards from '@/components/ScoreCards';
import BeamScoreInfo from '@/components/BeamScoreInfo';

const Results = () => {
  const [scoreBreakdown, setScoreBreakdown] = useState<ScoreBreakdown | null>(null);
  const [projectName, setProjectName] = useState('Unnamed Project');
  const [industry, setIndustry] = useState('Technology');
  const [showAdvancedView, setShowAdvancedView] = useState(false);
  const [showMethodology, setShowMethodology] = useState(false);
  const [showAIInsights, setShowAIInsights] = useState(false);
  const [responses, setResponses] = useState({});
  const [activeTab, setActiveTab] = useState('basic');
  const { toast } = useToast();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [saveToDatabase, setSaveToDatabase] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const responsesJson = sessionStorage.getItem('nev-responses');
        const projectName = sessionStorage.getItem('nev-project-name') || 'Unnamed Project';
        const industry = sessionStorage.getItem('nev-industry') || 'Technology';
        
        if (!responsesJson) {
          toast({
            title: t('results.noData'),
            description: t('results.noDataDesc'),
            variant: "destructive"
          });
          navigate('/industry');
          return;
        }

        const responses = JSON.parse(responsesJson);
        setProjectName(projectName);
        setIndustry(industry);
        setResponses(responses);

        const calculatedScores = calculateScoreBreakdown(responses);
        
        if (calculatedScores) {
          calculatedScores.overallScore = calculatedScores.overallScore || 0;
          calculatedScores.nev = calculatedScores.nev || calculatedScores.totalNEV || 0;
          calculatedScores.eroi = calculatedScores.eroi || 0;
          calculatedScores.categories = calculatedScores.categories || {};
          calculatedScores.recommendations = calculatedScores.recommendations || [];
          
          setScoreBreakdown(calculatedScores);
        }
        
        setSaveToDatabase(!!user);
        
        if (user && calculatedScores) {
          try {
            await saveSurveyResponse(projectName, industry, responses, calculatedScores);
            toast({
              title: t('results.saved'),
              description: t('results.savedDesc'),
            });
          } catch (error) {
            console.error('Error saving survey response:', error);
            toast({
              title: t('results.saveError'),
              description: t('results.saveErrorDesc'),
              variant: "destructive"
            });
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast({
          title: t('results.error'),
          description: t('results.errorDesc'),
          variant: "destructive"
        });
      }
    };

    fetchData();
  }, [toast, navigate, user, t]);

  const handleDownloadPdf = async () => {
    if (scoreBreakdown) {
      await generatePdf(scoreBreakdown, projectName, industry, language);
    } else {
      toast({
        title: t('results.noData'),
        description: t('results.noDataDownload'),
        variant: "destructive"
      });
    }
  };

  const beamScoreExplanation = t('results.understandingBeamScore') || "The BEAM Score measures emotional and strategic value across diverse asset classes on a scale of 0-100. Higher scores indicate exceptional emotional resonance, strong ethical alignment, and high adaptability potential.";
  
  const beamScoreApplications = [
    t('results.industryStandards') || "BEAM Score benchmarks your project against industry standards",
    t('results.improvementOpportunities') || "Identify emotional and brand value improvement opportunities",
    t('results.trackProgress') || "Track progress over time as you implement recommendations",
    t('results.compareProjects') || "Compare different projects across your portfolio",
    t('results.measureROI') || "Measure return on investment for emotional engagement initiatives"
  ];

  const nevExplanation = t('metrics.nev_description') || "NEV (Net Emotional Value) measures the overall emotional impact and brand resonance of your project. It reflects the strength of user emotional connection, brand loyalty, and community engagement. A higher NEV indicates a stronger positive emotional impact.";
  const eroiExplanation = t('metrics.eroi_description') || "EROI (Emotional Return on Investment) evaluates the transformative impact, long-term industry relevance, and legacy value of your project. It assesses how effectively your project converts emotional engagement into tangible actions like investment or advocacy.";

  const safeToFixed = (value, decimals = 2) => {
    if (value === undefined || value === null) return '0.00';
    return Number(value).toFixed(decimals);
  };

  const getTabContent = () => {
    if (showMethodology) {
      return <MethodologySection />;
    } else if (showAIInsights && Object.keys(responses).length > 0) {
      return (
        <AIInsights 
          scoreBreakdown={scoreBreakdown} 
          responses={responses}
          industry={industry}
          questions={questions}
        />
      );
    } else if (showAdvancedView) {
      return (
        <ProfessionalDashboard 
          scoreBreakdown={scoreBreakdown}
          industry={industry}
        />
      );
    } else {
      return <ResultsChart scoreBreakdown={scoreBreakdown} />;
    }
  };

  const getRiskBadgeColor = (rating) => {
    switch (rating) {
      case 'High Risk':
        return 'bg-red-600 text-white';
      case 'Competitive':
        return 'bg-amber-500 text-white';
      case 'Exceptional':
        return 'bg-green-600 text-white';
      default:
        return 'bg-gray-600 text-white';
    }
  };

  const getRiskIconColor = (rating) => {
    switch (rating) {
      case 'High Risk':
        return 'text-white';
      case 'Competitive':
        return 'text-white';
      case 'Exceptional':
        return 'text-white';
      default:
        return 'text-white';
    }
  };
  
  // Get certification badge for the sidebar
  const getCertificationBadge = (score) => {
    const scaledScore = score * 10; // Convert to 0-100 scale
    if (scaledScore >= 90) {
      return (
        <div className="flex items-center gap-1 bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
          <Trophy className="h-3.5 w-3.5 text-yellow-600" />
          BEAM Gold
        </div>
      );
    } else if (scaledScore >= 80) {
      return (
        <div className="flex items-center gap-1 bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium">
          <Award className="h-3.5 w-3.5 text-gray-500" />
          BEAM Silver
        </div>
      );
    } else if (scaledScore >= 70) {
      return (
        <div className="flex items-center gap-1 bg-amber-100 text-amber-800 px-2 py-1 rounded-full text-xs font-medium">
          <Star className="h-3.5 w-3.5 text-amber-600" />
          BEAM Bronze
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-white py-4 px-4 text-gray-800">
      <div className="max-w-7xl mx-auto">
        <div className="w-full flex justify-between items-center mb-6">
          <div className="flex items-center">
            <Logo className="mr-4" showVersion={true} size="md" />
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Button 
              variant="outline" 
              onClick={handleDownloadPdf} 
              size="sm" 
              className="bg-white border-gray-200 hover:bg-gray-50 text-gray-800"
            >
              {t('results.download')} <Download className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {scoreBreakdown ? (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-6">
              <div className="lg:col-span-3">
                <Card className="bg-white border-gray-200 shadow-sm mb-4">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-md font-bold text-gray-700">{t('results.title')}</CardTitle>
                    <CardDescription className="text-sm text-gray-500">
                      {projectName} • {industry}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="px-3">
                    <nav className="space-y-1">
                      <Button 
                        variant={!showAdvancedView && !showAIInsights && !showMethodology ? "default" : "ghost"}
                        className="w-full justify-start text-sm mb-1 hover:bg-gray-100"
                        onClick={() => {
                          setShowAdvancedView(false);
                          setShowAIInsights(false);
                          setShowMethodology(false);
                          setActiveTab('basic');
                        }}
                      >
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        {t('results.basicView')}
                      </Button>
                      <Button 
                        variant={showAdvancedView ? "default" : "ghost"}
                        className="w-full justify-start text-sm mb-1 hover:bg-gray-100"
                        onClick={() => {
                          setShowAdvancedView(true);
                          setShowAIInsights(false);
                          setShowMethodology(false);
                          setActiveTab('advanced');
                        }}
                      >
                        <BarChart2 className="mr-2 h-4 w-4" />
                        {t('results.advancedView')}
                      </Button>
                      <Button 
                        variant={showAIInsights ? "default" : "ghost"}
                        className="w-full justify-start text-sm mb-1 hover:bg-gray-100"
                        onClick={() => {
                          setShowAdvancedView(false);
                          setShowAIInsights(true);
                          setShowMethodology(false);
                          setActiveTab('ai');
                        }}
                      >
                        <Activity className="mr-2 h-4 w-4" />
                        {t('results.beamAIInsights')}
                      </Button>
                      <Button 
                        variant={showMethodology ? "default" : "ghost"}
                        className="w-full justify-start text-sm mb-1 hover:bg-gray-100"
                        onClick={() => {
                          setShowAdvancedView(false);
                          setShowAIInsights(false);
                          setShowMethodology(true);
                          setActiveTab('methodology');
                        }}
                      >
                        <Info className="mr-2 h-4 w-4" />
                        {t('results.beamMethodology')}
                      </Button>
                    </nav>
                  </CardContent>
                </Card>
                
                <Card className="bg-white border-gray-200 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-md font-bold text-gray-700">{t('results.beamEvaluateMetrics')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="bg-white p-3 rounded-lg border border-gray-200">
                        <div className="flex justify-between items-center mb-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center cursor-help">
                                  <span className="text-sm font-medium text-gray-700">BEAM Score</span>
                                  <HelpCircle className="h-3.5 w-3.5 ml-1 text-gray-400" />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p className="text-xs">{beamScoreExplanation}</p>
                                <p className="text-xs mt-1">Core weighted Emotion Resonance metric on a scale of 0-100</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-800 font-semibold">{safeToFixed(scoreBreakdown.overallScore * 10, 1)}</span>
                            {getCertificationBadge(scoreBreakdown.overallScore)}
                          </div>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${Math.min(100, (scoreBreakdown.overallScore * 10))}%` }}></div>
                        </div>
                      </div>
                      
                      <div className="bg-white p-3 rounded-lg border border-gray-200">
                        <div className="flex justify-between items-center mb-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center cursor-help">
                                  <span className="text-sm font-medium text-gray-700">{t('metrics.nev')}</span>
                                  <HelpCircle className="h-3.5 w-3.5 ml-1 text-gray-400" />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p className="text-xs">{nevExplanation}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <span className="text-gray-800 font-semibold">{safeToFixed(scoreBreakdown.nev)}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div className="bg-purple-500 h-2 rounded-full" style={{ width: `${Math.min(100, (scoreBreakdown.nev / 50) * 100)}%` }}></div>
                        </div>
                      </div>
                      
                      <div className="bg-white p-3 rounded-lg border border-gray-200">
                        <div className="flex justify-between items-center mb-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center cursor-help">
                                  <span className="text-sm font-medium text-gray-700">{t('metrics.eroi')}</span>
                                  <HelpCircle className="h-3.5 w-3.5 ml-1 text-gray-400" />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p className="text-xs">{eroiExplanation}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <span className="text-gray-800 font-semibold">{safeToFixed(scoreBreakdown.eroi)}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div className="bg-green-500 h-2 rounded-full" style={{ width: `${Math.min(100, (scoreBreakdown.eroi / 5) * 100)}%` }}></div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              <div className="lg:col-span-9">
                <Card className="bg-white border-gray-200 shadow-sm mb-6">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xl font-bold text-blue-600">
                      {t('results.beamEvaluateAnalysis')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        {scoreBreakdown.rating && (
                          <div className={`px-6 py-3 rounded-lg shadow-sm ${getRiskBadgeColor(scoreBreakdown.rating)} flex items-center gap-3`}>
                            <ShieldAlert className={`h-6 w-6 ${getRiskIconColor(scoreBreakdown.rating)}`} />
                            <div>
                              <div className="text-xs uppercase font-medium opacity-90">Risk Level</div>
                              <div className="font-semibold text-lg">
                                {scoreBreakdown.rating === 'High Risk' ? 'High Risk' : 
                                 scoreBreakdown.rating === 'Competitive' ? 'Competitive' : 
                                 t(`risk.${scoreBreakdown.rating.toLowerCase().replace(' ', '')}`)}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {!showAIInsights && !showAdvancedView && !showMethodology && (
                      <>
                        <ScoreCards scoreBreakdown={scoreBreakdown} />
                        <BeamScoreInfo />
                      </>
                    )}
                    
                    {scoreBreakdown.keyInsight && (
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-100 mb-4">
                        <div className="flex gap-2">
                          <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-gray-700">{scoreBreakdown.keyInsight}</p>
                        </div>
                      </div>
                    )}
                    
                    {getTabContent()}
                  </CardContent>
                </Card>
              </div>
            </div>
          </>
        ) : (
          <Card className="bg-white border-gray-200 shadow-sm mb-6">
            <CardContent className="py-8">
              <div className="flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mb-4"></div>
                <p className="text-gray-500">{t('results.loading')}</p>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex justify-between">
          <Button 
            variant="outline" 
            onClick={() => navigate('/survey')}
            className="bg-white border-gray-200 hover:bg-gray-50 text-gray-800"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> {t('results.backToSurvey')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Results;
